// src/layouts/AppLayout.jsx
// Responsive layout with mobile navigation and AI assistant

import React, { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import MobileNav from "../components/MobileNav";
import AIAssistant from "../components/AIAssistant";
import InstallPrompt from "../components/InstallPrompt";
import AgentSwitcher from "../components/AgentSwitcher";
import ActingAsBanner from "../components/ActingAsBanner";
import OnboardingBanner from "../components/OnboardingBanner";
import { AIProvider } from "../context/AIContext";
import { useAuth } from "../context/AuthContext";
import { clearToken } from "../auth/Auth";
import {
  registerServiceWorker,
  subscribeToPush,
  isPushSupported,
  isSubscribedToPush
} from "../utils/pushNotifications";

// Roles that should auto-enable push notifications
const PUSH_AUTO_ENABLE_ROLES = ['agent', 'agency', 'agency_owner', 'agency_admin', 'founder', 'fmo'];

export default function AppLayout() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();

  // Register service worker and auto-enable push notifications for agents
  useEffect(() => {
    async function setupPushNotifications() {
      try {
        // Register service worker first
        await registerServiceWorker();

        // Only auto-enable for allowed roles
        if (!user?.id || !PUSH_AUTO_ENABLE_ROLES.includes(user.role)) {
          return;
        }

        // Check if push is supported
        if (!isPushSupported()) {
          console.log('[AppLayout] Push not supported in this browser');
          return;
        }

        // Check if already subscribed
        const alreadySubscribed = await isSubscribedToPush();
        if (alreadySubscribed) {
          console.log('[AppLayout] Already subscribed to push');
          return;
        }

        // Check if we've already asked (don't annoy user on every page load)
        const pushAsked = localStorage.getItem('push-auto-asked');
        if (pushAsked) {
          return;
        }

        // Mark that we've asked (so we only ask once)
        localStorage.setItem('push-auto-asked', 'true');

        // Auto-subscribe (this will prompt for permission)
        console.log('[AppLayout] Auto-enabling push notifications for agent');
        const result = await subscribeToPush();

        if (result.success) {
          console.log('[AppLayout] Push notifications enabled automatically');
        } else {
          console.log('[AppLayout] Push auto-enable failed:', result.error);
        }
      } catch (err) {
        console.log('[AppLayout] Push setup error:', err.message);
      }
    }

    setupPushNotifications();
  }, [user?.id, user?.role]);

  function onLogout() {
    clearToken();
    nav("/login", { replace: true });
  }

  return (
    <AIProvider>
      <div className="min-h-screen bg-gray-100">
        {/* "Acting as agent" overlay banner - sticky at very top */}
        <ActingAsBanner />

        {/* Onboarding setup progress banner */}
        <OnboardingBanner />

        {/* Mobile Navigation - only visible on mobile */}
        <MobileNav />

        <div className="flex">
          {/* Desktop Sidebar - hidden on mobile */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="fixed w-64 h-screen overflow-y-auto overflow-x-hidden">
              <Sidebar />
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Desktop Top bar - hidden on mobile (mobile has its own header) */}
            <div className="hidden lg:flex h-14 bg-white border-b items-center justify-between px-4 sticky top-0 z-30">
              <div className="text-sm text-gray-600">{t("welcomeBack")}</div>
              <div className="flex items-center gap-3">
                <AgentSwitcher />
                <button
                  onClick={onLogout}
                  className="text-sm px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  {t("logOut")}
                </button>
              </div>
            </div>

            {/* Page content - responsive padding */}
            <div className="p-3 sm:p-4 lg:p-6">
              <Outlet />
            </div>
          </main>
        </div>

        {/* Global AI Assistant - accessible from anywhere */}
        <AIAssistant />

        {/* PWA Install Prompt - only shown to agents/admins */}
        <InstallPrompt />
      </div>
    </AIProvider>
  );
}
